import React from 'react';
import type { FC } from 'react';
import kebabCase from 'lodash/kebabCase.js';
import { TextInput } from '@wix/cli-ui-kit';
import { useTranslation } from '@wix/cli-i18n';
import { relativeFolderName } from '@wix/cli-fs';
import { getDashboardModalsDir } from '@wix/cli-app-definitions';
import { useProjectModel } from '../../../model.js';
import {
  composeValidations,
  pathValidation,
  useValidation,
} from '../../../dev-center/components/validations/index.js';
import { useExtensions } from '../../extensions/useExtensions.js';
import { useWixCliAppBi } from '../../../bi/index.js';

import { useWizardStep } from '../Wizard.js';

export const ChooseTitleStep: FC<{ onSubmit: (value: string) => void }> = ({
  onSubmit,
}) => {
  const bi = useWixCliAppBi();
  const { t } = useTranslation();
  const { isActive, goToNextStep } = useWizardStep();
  const extensionType = t(
    'generate_command.flow.dashboard_modal'
  ).toLowerCase();

  return (
    <TextInput
      isActive={isActive}
      label={t('generate_command.dashboard_modal.choose_title')}
      placeholder={t('generate_command.dashboard_modal.title_placeholder')}
      onSubmit={(modalTitle) => {
        bi.cliFlowStepAnswered({
          question: t('generate_command.dashboard_modal.choose_title'),
          questionKey: 'generate_command.dashboard_modal.choose_title',
          answer: modalTitle,
          additionalInfo: { extensionType },
        });
        onSubmit(modalTitle);
        goToNextStep();
      }}
    />
  );
};

export const ChooseFolderStep: FC<{
  onSubmit: (value: string) => void;
  modalFolder: string | undefined;
  modalTitle: string | undefined;
}> = ({ onSubmit, modalTitle, modalFolder }) => {
  const bi = useWixCliAppBi();
  const { t } = useTranslation();
  const { model } = useProjectModel();
  const { getExtensions } = useExtensions();
  const { isActive, goToNextStep } = useWizardStep();
  const dashboardModals = getExtensions('DashboardModal');
  const extensionType = t(
    'generate_command.flow.dashboard_modal'
  ).toLowerCase();

  const defaultModalFolder = kebabCase(modalTitle);

  const validateDashboardFolder = useValidation(
    composeValidations([
      [
        (folder: string) => {
          return dashboardModals.every(
            (modal) =>
              relativeFolderName(
                getDashboardModalsDir(model.projectFolder),
                modal.relativePath
              ) !== folder
          );
        },
        t('generate_command.dashboard_modal.validation.folder_exists'),
      ],
      [
        pathValidation,
        t('generate_command.dashboard_modal.validation.folder_invalid'),
      ],
    ])
  );

  return (
    <TextInput
      isActive={isActive}
      label={t('generate_command.dashboard_modal.choose_folder')}
      placeholder={modalFolder ?? defaultModalFolder}
      onSubmit={(value) => {
        bi.cliFlowStepAnswered({
          question: t('generate_command.dashboard_modal.choose_folder'),
          questionKey: 'generate_command.dashboard_modal.choose_folder',
          answer: value,
          additionalInfo: { extensionType },
        });
        onSubmit(value);
        goToNextStep();
      }}
      validate={validateDashboardFolder}
    />
  );
};
